import React, { useCallback, useMemo, useState } from 'react';
import { Formik, Form, Field as FormikField } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';

// NOTE: renderInput ko Formik-friendly bana diya.
// Agar aapke project me existing renderInput ka Formik version hai to use import kar lo.
const renderInput = ({ field, form, ...props }) => {
  const hasError = form.touched[field.name] && form.errors[field.name];
  return (
    <div className={`form-group ${hasError ? 'has-error' : ''}`}>
      <input {...field} {...props} className={`form-control ${props.className || ''}`} />
      {hasError ? <span className="help-block">{form.errors[field.name]}</span> : null}
    </div>
  );
};

const validationSchema = Yup.object({
  qualityLevel: Yup.number()
    .typeError('Enter 1 or 2')
    .min(1, 'Min 1')
    .max(2, 'Max 2')
    .required('Required'),
  // video (link) optional because user can choose Upload mode
  video: Yup.string().url('Invalid URL').nullable().notRequired(),
});

const CreatePieceVideo = (props) => {
  const { isCreating, pieceInstrumentInfo, accessMode } = props;

  // ----- Local UI state (non-formik) -----
  const [awsKeys, setAwsKeys] = useState({});
  const [file, setFile] = useState(null);
  const [disableUpload, setDisableUpload] = useState(false);
  const [videoUploaded, setVideoUploaded] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [videoInputOn, setVideoInputOn] = useState(false); // false = Link, true = Upload

  // Edit mode states
  const [showFollowing, setShowFollowing] = useState(false);
  const [videoInputEditOn, setVideoInputEditOn] = useState(false);
  const [videoToEdit, setVideoToEdit] = useState(null);
  const [awsKeysEdit, setAwsKeysEdit] = useState({}); // separate aws fields for edit

  const piece = pieceInstrumentInfo?.piece;
  const videos = piece?.videos || [];

  const initialValues = useMemo(
    () => ({
      qualityLevel: '',
      video: '', // link string
    }),
    []
  );

  const resetLocalState = useCallback(() => {
    setAwsKeys({});
    setAwsKeysEdit({});
    setFile(null);
    setDisableUpload(false);
    setVideoUploaded(false);
    setIsUploading(false);
    setVideoInputOn(false);
    setVideoInputEditOn(false);
    setShowFollowing(false);
    setVideoToEdit(null);
    try {
      const el = document.getElementById('uploadPieceVideo');
      if (el) el.value = null;
    } catch { }
  }, []);

  // ---------- Handlers ----------
  const onFileChange = async (e, isEdit = false) => {
    setVideoUploaded(false);
    setDisableUpload(false);

    const f = e.target.files?.[0];
    if (!f) return;

    setFile(f);
    const fileName = f.name.replace(/ /g, '_');
    try {
      const keys = await pieceInstrumentInfo.getAwsKey({ key: `pieces/videos/${fileName}` });
      if (isEdit) {
        setAwsKeysEdit(keys);
        // also prime videoToEdit.video_file for final save path (action+key)
        setVideoToEdit((prev) => (prev ? { ...prev, video_file: `${keys.action}${keys.key}` } : prev));
      } else {
        setAwsKeys(keys);
      }
      setDisableUpload(true);
    } catch (err) {
      toast.error('Failed to get upload key. Please try again.');
    }
  };

  const uploadVideo = (label, values, formikHelpers) => async (e) => {
    e.preventDefault();

    const isEdit = label === 'editVideo';
    const currentAws = isEdit ? awsKeysEdit : awsKeys;

    // if in upload mode but aws missing
    if ((isEdit ? videoInputEditOn : videoInputOn) && !currentAws?.action) {
      toast.error('Please try again.');
      return;
    }

    // prepare S3 formdata if aws present
    let data = null;
    const formId = isEdit ? 'videoUploadEdit' : 'videoUpload';
    if (Object.keys(currentAws || {}).length) {
      const formEl = document.getElementById(formId);
      if (formEl) data = new FormData(formEl);
    }

    try {
      setIsUploading(true);
      if (Object.keys(currentAws || {}).length && data) {
        await fetch(currentAws.action, { method: 'POST', body: data });
      }

      toast.success('Video uploaded successfully.');
      setVideoUploaded(true);

      if (isEdit) {
        await editVideo();
      } else {
        await onSubmit(values, formikHelpers);
      }
    } catch (err) {
      toast.error('Something went wrong while uploading video.');
    } finally {
      setIsUploading(false);
    }
  };

  const onSubmit = async (values, { resetForm }) => {
    const piece_id = piece?.id;
    if (!piece_id) return;

    const quality_level = parseInt(values.qualityLevel, 10);

    // If upload mode, video link becomes awsKeys.action+key (set during onFileChange)
    const video_file = videoInputOn
      ? `${awsKeys?.action || ''}${awsKeys?.key || ''}`
      : (values.video || '').trim();

    await props.onCreate({ piece_id, video_file, quality_level });

    pieceInstrumentInfo.getCurrentPiece('edit', piece_id);

    resetForm();
    resetLocalState();
  };

  const editVideo = async () => {
    if (!videoToEdit) return;
    const piece_id = piece?.id;
    await props.onEdit(videoToEdit, videos[showFollowing]);
    pieceInstrumentInfo.getCurrentPiece('edit', piece_id);

    // reset edit states
    setShowFollowing(false);
    setVideoToEdit(null);
    setVideoInputEditOn(false);
    setAwsKeysEdit({});
    setIsUploading(false);
  };

  const onDelete = async (video) => {
    await props.onDelete(video);
    const piece_id = piece?.id;
    pieceInstrumentInfo.getCurrentPiece('edit', piece_id);
  };

  const onShowEdit = (i) => {
    const videoObj = videos[i];
    if (!videoObj) return;
    setShowFollowing(i);
    setVideoToEdit(JSON.parse(JSON.stringify(videoObj)));
    setVideoInputEditOn(false);
    setAwsKeysEdit({});
    try {
      const el = document.getElementById('uploadPieceVideo');
      if (el) el.value = null;
    } catch { }
  };

  // ---------- Render existing videos ----------
  const renderPieceVideos = () => {
    if (!piece || !videos.length) return null;

    return videos.map((video, i) => (
      <div className="instrument-list" key={video.id}>
        {accessMode === 'edit' && (
          <div className="row">
            <span
              style={{ display: 'inline-block', float: 'right', color: 'red', cursor: 'pointer' }}
              className="glyphicon glyphicon-remove-circle"
              aria-hidden="true"
              onClick={() => onDelete(video)}
            />
            <span
              style={{ display: 'inline-block', float: 'right', marginRight: '10px', color: 'green', cursor: 'pointer' }}
              className="glyphicon glyphicon-edit"
              aria-hidden="true"
              onClick={() => onShowEdit(i)}
            />
          </div>
        )}

        {showFollowing !== i && (
          <div>
            <div className="row">
              <div className="col-md-3">
                <label>Quality</label>
              </div>
              <div className="col-md-9">{video.quality_level}</div>
            </div>
            <div className="row">
              <div className="col-md-3">
                <label>Video File</label>
              </div>
              <div className="col-md-9">{video.video_file}</div>
            </div>
            <hr />
          </div>
        )}

        {showFollowing === i && videoToEdit && (
          <div>
            <div className="row">
              <div className="col-md-3">
                <label>Quality</label>
              </div>
              <div className="col-md-9">
                <input
                  name="qualityLevelEdit"
                  placeholder="Quality Level"
                  type="number"
                  min={1}
                  max={2}
                  value={videoToEdit.quality_level}
                  onChange={(e) =>
                    setVideoToEdit((prev) => ({ ...prev, quality_level: Number(e.target.value) }))
                  }
                  className="form-control"
                />
              </div>
            </div>

            <div className="row">
              <div className="col-md-3">
                <label>Video File</label>
              </div>
              <div className="col-md-9 radio-part">
                <div className="currentFile">{videoToEdit.video_file}</div>

                <div className="radio-selection row">
                  <div className="col-md-4">
                    <input
                      type="radio"
                      id="linkTypeVideoEdit"
                      name={`videoTypeEdit_${i}`}
                      checked={!videoInputEditOn}
                      onChange={() => {
                        setVideoInputEditOn(false);
                        setAwsKeysEdit({});
                      }}
                    />
                    &nbsp;
                    <label htmlFor="linkTypeVideoEdit">Link</label>
                  </div>
                  <div className="col-md-4">
                    <input
                      type="radio"
                      id="uploadTypeVideoEdit"
                      name={`videoTypeEdit_${i}`}
                      checked={!!videoInputEditOn}
                      onChange={() => {
                        setVideoInputEditOn(true);
                        setAwsKeysEdit({});
                        // Clear chosen file control if any
                        try {
                          const el = document.getElementById('uploadPieceVideo');
                          if (el) el.value = null;
                        } catch { }
                      }}
                    />
                    &nbsp;
                    <label htmlFor="uploadTypeVideoEdit">Upload</label>
                  </div>
                </div>

                {videoInputEditOn ? (
                  <form id="videoUploadEdit" method="post" encType="multipart/form-data">
                    <input type="hidden" name="key" value={awsKeysEdit.key || 'NA'} />
                    <input type="hidden" name="acl" value={awsKeysEdit.acl || 'NA'} />
                    <input type="hidden" name="X-Amz-Credential" value={awsKeysEdit['x-amz-credential'] || 'NA'} />
                    <input type="hidden" name="X-Amz-Algorithm" value={awsKeysEdit['x-amz-algorithm'] || 'NA'} />
                    <input type="hidden" name="X-Amz-Date" value={awsKeysEdit['x-amz-date'] || 'NA'} />
                    <input type="hidden" name="Policy" value={awsKeysEdit.policy || 'NA'} />
                    <input type="hidden" name="X-Amz-Signature" value={awsKeysEdit['x-amz-signature'] || 'NA'} />
                    <input
                      id="uploadPieceVideo"
                      type="file"
                      name="file"
                      onChange={(e) => onFileChange(e, true)}
                    />
                  </form>
                ) : (
                  <input
                    name="video"
                    placeholder="Video File"
                    value={videoToEdit.video_file}
                    onChange={(e) =>
                      setVideoToEdit((prev) => ({ ...prev, video_file: e.target.value }))
                    }
                    className={`form-control ${accessMode === 'view' ? 'disabled' : ''}`}
                  />
                )}
              </div>
            </div>

            <div className="editButtons">
              <button
                type="submit"
                name="submit"
                className="cus-btn-primary btn-sm submitButton"
                style={{ margin: '10px' }}
                onClick={uploadVideo('editVideo')}
              >
                {isCreating || isUploading ? <span className="spinner" /> : <span>Save</span>}
              </button>
              <button
                type="button"
                name="button"
                className="cus-btn-danger btn-sm submitButton"
                style={{ margin: '10px' }}
                onClick={() => {
                  setShowFollowing(false);
                  setVideoToEdit(null);
                  setVideoInputEditOn(false);
                  setAwsKeysEdit({});
                }}
              >
                {isCreating || isUploading ? <span className="spinner" /> : <span>Cancel</span>}
              </button>
            </div>
            <hr />
          </div>
        )}
      </div>
    ));
  };

  // ---------- Main render ----------
  return (
    <section className="well create-instrument">
      {renderPieceVideos()}

      {accessMode === 'edit' &&
        piece &&
        ((videos && videos.length) < 2 || !videos.length) && (
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
          >
            {(formik) => (
              <Form>
                <div className="row">
                  <div className="col-md-3">
                    <label>Quality</label>
                  </div>
                  <div className="col-md-9">
                    <FormikField
                      name="qualityLevel"
                      placeholder="Enter 1 or 2"
                      type="number"
                      min={1}
                      max={2}
                      component={renderInput}
                    />
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-3">
                    <label>Video File</label>
                  </div>
                  <div className="col-md-9 radio-part">
                    <div className="radio-selection row">
                      <div className="col-md-4">
                        <input
                          type="radio"
                          id="linkTypeVideo"
                          name="videoType"
                          checked={!videoInputOn}
                          onChange={() => {
                            setVideoInputOn(false);
                            setAwsKeys({});
                            setDisableUpload(false);
                            setFile(null);
                          }}
                        />
                        &nbsp;
                        <label htmlFor="linkTypeVideo">Link</label>
                      </div>
                      <div className="col-md-4">
                        <input
                          type="radio"
                          id="uploadTypeVideo"
                          name="videoType"
                          checked={!!videoInputOn}
                          onChange={() => {
                            setVideoInputOn(true);
                            setAwsKeys({});
                            setDisableUpload(false);
                            setFile(null);
                            try {
                              const el = document.getElementById('uploadPieceVideo');
                              if (el) el.value = null;
                            } catch { }
                          }}
                        />
                        &nbsp;
                        <label htmlFor="uploadTypeVideo">Upload</label>
                      </div>
                    </div>

                    {videoInputOn ? (
                      <form id="videoUpload" method="post" encType="multipart/form-data">
                        <input type="hidden" name="key" value={awsKeys.key || 'NA'} />
                        <input type="hidden" name="acl" value={awsKeys.acl || 'NA'} />
                        <input type="hidden" name="X-Amz-Credential" value={awsKeys['x-amz-credential'] || 'NA'} />
                        <input type="hidden" name="X-Amz-Algorithm" value={awsKeys['x-amz-algorithm'] || 'NA'} />
                        <input type="hidden" name="X-Amz-Date" value={awsKeys['x-amz-date'] || 'NA'} />
                        <input type="hidden" name="Policy" value={awsKeys.policy || 'NA'} />
                        <input type="hidden" name="X-Amz-Signature" value={awsKeys['x-amz-signature'] || 'NA'} />
                        <input
                          id="uploadPieceVideo"
                          type="file"
                          name="file"
                          onChange={(e) => onFileChange(e, false)}
                        />
                      </form>
                    ) : (
                      <FormikField
                        name="video"
                        placeholder="Video File"
                        component={renderInput}
                        disabled={accessMode === 'view'}
                      />
                    )}
                  </div>
                </div>

                <button
                  type="button"
                  name="submit"
                  value="Upload to Amazon S3"
                  className="cus-btn-primary btn-block btn-lg submitButton"
                  disabled={
                    isCreating ||
                    !piece ||
                    !formik.values.qualityLevel ||
                    (videoInputOn ? !awsKeys?.key : !formik.values.video)
                  }
                  onClick={uploadVideo('newVideo', formik.values, formik)}
                >
                  {isCreating || isUploading ? (
                    <span className="spinner" />
                  ) : (
                    <span>
                      {Number(formik.values.qualityLevel) === 2 ? 'Add Sheet Video' : 'Add Piece Video'}
                    </span>
                  )}
                </button>
              </Form>
            )}
          </Formik>
        )}
    </section>
  );
};

export default CreatePieceVideo;
